import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade3EngineeringLesson.css";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

/** Helper to shuffle arrays */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * 3D Engineering Demo:
 *  Cycles among “Car”, “Plane”, and “Wind Turbine”
 *  to illustrate different problem-solving examples.
 */
function Engineering3DDemo() {
  const items = [
    {
      name: "Car",
      description: "A car’s aerodynamic design helps it move faster!",
      example: "Engineers reduce drag to improve speed.",
    },
    {
      name: "Plane",
      description: "Airplanes solve traveling long distances quickly!",
      example: "Engineers design wings to provide lift in the air.",
    },
    {
      name: "Wind Turbine",
      description: "Wind turbines capture wind energy to create electricity!",
      example: "Engineers shape blades to maximize efficiency.",
    },
  ];

  const [index, setIndex] = useState(0);

  const nextItem = () => {
    setIndex((prev) => (prev + 1) % items.length);
  };

  return (
    <div className="g1eng-simple-machine-container" style={{ marginTop: "20px" }}>
      <h4>3D Engineering Demo: {items[index].name}</h4>
      <p className="g1eng-machine-description">{items[index].description}</p>
      <p className="g1eng-machine-example">
        <strong>Fun Fact:</strong> {items[index].example}
      </p>
      <button className="g1eng-next-machine-btn" onClick={nextItem}>
        See Another Example
      </button>

      <div style={{ width: "300px", height: "200px", margin: "10px auto", background: "#f0f0f0" }}>
        <Canvas camera={{ position: [0, 2, 5], fov: 50 }}>
          <OrbitControls enablePan={false} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} />
          {/* Ground plane */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1, 0]}>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color="#ddd" />
          </mesh>

          {/* 3D model (Car, Plane, or Turbine) */}
          <EngineeringShape name={items[index].name} />
        </Canvas>
      </div>
    </div>
  );
}

/**
 * EngineeringShape:
 * Renders a Car, Plane, or Wind Turbine in 3D.
 */
function EngineeringShape({ name }) {
  if (name === "Car") {
    return (
      <group>
        {/* Car body as a box */}
        <mesh>
          <boxGeometry args={[1.5, 0.5, 0.8]} />
          <meshStandardMaterial color="#ff7043" />
        </mesh>
        {/* Wheels (spheres) */}
        <mesh position={[-0.5, -0.3, 0.5]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="black" />
        </mesh>
        <mesh position={[0.5, -0.3, 0.5]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="black" />
        </mesh>
        <mesh position={[-0.5, -0.3, -0.5]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="black" />
        </mesh>
        <mesh position={[0.5, -0.3, -0.5]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="black" />
        </mesh>
      </group>
    );
  }

  else if (name === "Plane") {
    return (
      <group>
        {/* Plane fuselage as a cylinder, rotated horizontally */}
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.15, 0.15, 2, 16]} />
          <meshStandardMaterial color="#7986cb" />
        </mesh>
        {/* Wings as a flat box */}
        <mesh>
          <boxGeometry args={[1.5, 0.05, 0.4]} />
          <meshStandardMaterial color="#3949ab" />
        </mesh>
        {/* Tail fin as a vertical rectangle at the back */}
        <mesh position={[0.8, 0.3, 0]}>
          <boxGeometry args={[0.02, 0.4, 0.3]} />
          <meshStandardMaterial color="#303f9f" />
        </mesh>
      </group>
    );
  }

  else if (name === "Wind Turbine") {
    return (
      <group>
        {/* Tower */}
        <mesh>
          <cylinderGeometry args={[0.1, 0.1, 2, 16]} />
          <meshStandardMaterial color="#9e9e9e" />
        </mesh>
        {/* Nacelle (top housing) */}
        <mesh position={[0, 1, 0]}>
          <boxGeometry args={[0.3, 0.2, 0.2]} />
          <meshStandardMaterial color="#757575" />
        </mesh>
        {/* Blades (three flat boxes) */}
        <group position={[0, 1, 0]}>
          {/* 1st blade */}
          <mesh position={[0.2, 0, 0]}>
            <boxGeometry args={[0.4, 0.05, 0.05]} />
            <meshStandardMaterial color="#cfd8dc" />
          </mesh>
          {/* 2nd blade - rotate 120 deg */}
          <mesh rotation={[(2 * Math.PI) / 3, 0, 0]}>
            <boxGeometry args={[0.4, 0.05, 0.05]} />
            <meshStandardMaterial color="#cfd8dc" />
          </mesh>
          {/* 3rd blade - rotate -120 deg */}
          <mesh rotation={[(-2 * Math.PI) / 3, 0, 0]}>
            <boxGeometry args={[0.4, 0.05, 0.05]} />
            <meshStandardMaterial color="#cfd8dc" />
          </mesh>
        </group>
      </group>
    );
  }

  // Default: no shape
  return null;
}

/**
 * EngineeringChallenge:
 * Rotates among different challenge prompts.
 */
function EngineeringChallenge() {
  const challenges = [
    {
      name: "Build a Strong Bridge",
      description: "How can you make a bridge strong enough to hold weight?",
      example: "Use triangles for support!",
    },
    {
      name: "Design a Windproof House",
      description: "What shape keeps houses safe from strong winds?",
      example: "A dome shape spreads the wind’s force!",
    },
    {
      name: "Make a Fast Car",
      description: "How can you make a car go faster?",
      example: "Make it light and aerodynamic!",
    },
  ];
  const [currentIndex, setCurrentIndex] = useState(0);

  const showNextChallenge = () => {
    setCurrentIndex((prev) => (prev + 1) % challenges.length);
  };

  return (
    <div className="g1eng-simple-machine-container" style={{ marginTop: "20px" }}>
      <h4>Engineering Challenge: {challenges[currentIndex].name}</h4>
      <p className="g1eng-machine-description">{challenges[currentIndex].description}</p>
      <p className="g1eng-machine-example">
        <strong>Tip:</strong> {challenges[currentIndex].example}
      </p>
      <button className="g1eng-next-machine-btn" onClick={showNextChallenge}>
        See Another Challenge
      </button>
    </div>
  );
}

/** Main Grade3EngineeringLesson component */
export default function Grade3EngineeringLesson() {
  const [selectedTopic, setSelectedTopic] = useState(null);

  /** LESSON CONTENT */
  const lessonContent = {
    problemSolving: {
      title: "Problem Solving in Engineering",
      description: "Engineers solve problems to make life better!",
      example: "Example: Designing airplanes to help people travel faster!",
      steps: [
        "Engineers identify a problem or need.",
        "They brainstorm creative solutions.",
        "They test and improve their designs multiple times!",
        "They also share ideas and collaborate to find the best approach.",
        <Engineering3DDemo key="demo-ps" />,
      ],
    },
    engineeringDesign: {
      title: "Engineering Design Process",
      description: "Learn the steps to design and build something new!",
      example: "Example: Making a strong and safe bridge!",
      steps: [
        "Ask: What is the problem or goal?",
        "Imagine: Think of creative ideas and solutions.",
        "Plan: Draw and design a possible solution on paper.",
        "Create: Build a working model or prototype.",
        "Improve: Test and make it better repeatedly!",
        <EngineeringChallenge key="challenge-edp" />,
      ],
    },
  };

  /** QUIZ POOLS (25 each => 50 total) */
  // 1) Problem Solving Pool
  const problemSolvingPool = [
    {
      question: "What is the first step in solving a problem?",
      correctAnswer: "Identify the problem or need",
      options: ["Guess a random fix", "Identify the problem or need", "Ignore it forever"],
    },
    {
      question: "Why do engineers brainstorm many ideas?",
      correctAnswer: "To find the best solution",
      options: ["They like to waste time", "They only want one idea", "To find the best solution"],
    },
    // ... (fill in the rest of your 25 questions)
    {
      question: "A big part of engineering problem solving is?",
      correctAnswer: "Learning from mistakes and adjusting",
      options: [
        "Never changing the plan",
        "Refusing all feedback",
        "Learning from mistakes and adjusting",
      ],
    },
  ];

  // 2) Engineering Design Pool
  const engineeringDesignPool = [
    {
      question: "What is the first step of the engineering design process?",
      correctAnswer: "Ask: What is the problem or goal?",
      options: ["Skip it", "Ask: What is the problem or goal?", "Draw random shapes first"],
    },
    {
      question: "Why do engineers imagine multiple ideas?",
      correctAnswer: "To explore different possibilities",
      options: ["They get bored", "To explore different possibilities", "They only want one solution"],
    },
    // ... (fill in the rest of your 25 questions)
    {
      question: "Engineers might do multiple design cycles because?",
      correctAnswer: "Each cycle can bring them closer to an ideal solution",
      options: [
        "They can’t make up their mind",
        "It’s required by law",
        "Each cycle can bring them closer to an ideal solution",
      ],
    },
  ];

  /** Quick Quiz + Final Quiz states */
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 min

  /** Timer for final quiz */
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      // time up
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  /** Generate a Quick Quiz question for the selected topic */
  const generateQuiz = (topic) => {
    const questionPool = topic === "problemSolving" ? problemSolvingPool : engineeringDesignPool;
    const randomQ = questionPool[Math.floor(Math.random() * questionPool.length)];
    setQuiz({
      question: randomQ.question,
      correctAnswer: randomQ.correctAnswer,
      options: shuffleArray([...randomQ.options]),
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  /** Quick Quiz: handle user answer */
  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    // optionally auto-refresh question after a delay
    setTimeout(() => {
      if (selectedTopic) generateQuiz(selectedTopic);
    }, 1500);
  };

  /** Final Quiz: start with all 50 questions */
  const startFinalQuiz = () => {
    const allQ = [...problemSolvingPool, ...engineeringDesignPool];
    const finalSet = shuffleArray(allQ); // randomize entire 50
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  /** Final Quiz: handle user answer, move next or finish */
  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex]?.correctAnswer === choice) {
      setFinalQuizScore((prev) => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex((prev) => prev + 1);
    } else {
      // done
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g1eng-page">
      <h1 className="g1eng-title">Grade 3 Engineering Lessons</h1>
      <p className="g1eng-description">Click a topic to learn more:</p>

      {/* Topic Buttons */}
      <div className="g1eng-topics">
        <button
          className="g1eng-topic-card"
          onClick={() => {
            setSelectedTopic("problemSolving");
            generateQuiz("problemSolving");
          }}
        >
          Problem Solving
        </button>
        <button
          className="g1eng-topic-card"
          onClick={() => {
            setSelectedTopic("engineeringDesign");
            generateQuiz("engineeringDesign");
          }}
        >
          Engineering Design
        </button>
      </div>

      {/* Lesson & Quizzes */}
      {selectedTopic && (
        <div className="g1eng-lesson-quiz-container">
          {/* Lesson Content (Left) */}
          <div className="g1eng-lesson-content">
            <h2 className="g1eng-lesson-title">{lessonContent[selectedTopic].title}</h2>
            <p className="g1eng-lesson-description">{lessonContent[selectedTopic].description}</p>
            <p className="g1eng-lesson-example">
              <strong>{lessonContent[selectedTopic].example}</strong>
            </p>
            <ul className="g1eng-lesson-steps">
              {lessonContent[selectedTopic].steps.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ul>
          </div>

          {/* Quizzes (Right) */}
          <div className="g1eng-quiz-double-container">
            {/* Quick Quiz */}
            <div className="g1eng-quick-quiz-container">
              <h2 className="g1eng-quiz-title">Quick Quiz!</h2>
              {quiz && (
                <>
                  <p className="g1eng-quiz-question">{quiz.question}</p>
                  <div className="g1eng-quiz-options">
                    {quiz.options.map((opt, i) => (
                      <button
                        key={i}
                        className={`g1eng-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                        onClick={() => handleAnswerClick(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {feedback && <p className="g1eng-quiz-feedback">{feedback}</p>}
                </>
              )}
            </div>

            {/* Final Quiz */}
            <div className="g1eng-final-quiz-container">
              <h2 className="g1eng-quiz-title">Final Quiz</h2>
              {!finalQuizActive && (
                <button className="g1eng-final-quiz-button" onClick={startFinalQuiz}>
                  Start Final Quiz (50 Questions)
                </button>
              )}
              {finalQuizActive && (
                <div className="g1eng-final-quiz-content">
                  <p>
                    Time Left: {Math.floor(timeLeft / 60)}:
                    {String(timeLeft % 60).padStart(2, "0")}
                  </p>
                  <p>
                    Question {finalQuizIndex + 1} of {finalQuizQuestions.length}
                  </p>
                  <p className="g1eng-quiz-question">
                    {finalQuizQuestions[finalQuizIndex].question}
                  </p>
                  <div className="g1eng-quiz-options">
                    {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                      <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                        {choice}
                      </button>
                    ))}
                  </div>
                  <p>Score: {finalQuizScore}</p>
                  {finalQuizIndex === finalQuizQuestions.length - 1 && (
                    <p>
                      Final Score: {finalQuizScore}/{finalQuizQuestions.length}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Back Button */}
      <Link to="/engineering" className="g1eng-back-button">
        ← Back to Engineering Page
      </Link>
    </div>
  );
}
